import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useLocaleContext } from "src/context/locale-context";
import { ProductSection } from "src/components/features/home/ProductSection/ProductSection";
import { VacationIcon } from "src/components/features/home/ProductSection/icons/VacationIcon";
import { PalmDecoration } from "./PalmDecoration";
import classes from "./CharterDealsCarousel.module.css";
import { CharterDealCard } from "./CharterDealCard";
import { charterDeals as mockCharterDeals } from "./charterDeals";
import { getCharterText } from "./charterDealsText";

const VISIBLE_COUNT = 4; // cards visible at once on desktop — matches the .cardWrap 4-up CSS

// This is the first unified product section on the page (right after the white search/benefits
// area) — a clearly warm cream/gold gradient (not flat beige) so it reads as a distinct "vacation"
// section, with two faded corner palm silhouettes for a tropical identity, instead of the shared
// ProductSection's generic fade-in/out accent band.
// Holds solid white for the last ~12% instead of still being cream right up to the section's own
// edge — gives a real white "buffer" before the next section's color starts, instead of a seam
// where this section's cream and the next section's tint sit almost directly against each other.
const SECTION_STYLE = {
  background:
    "linear-gradient(180deg, #fffdf8 0%, #fff6e6 15%, #fff3dd 45%, #fff8ee 68%, #ffffff 85%, #ffffff 100%)",
  marginTop: "1.25rem", // 20px
  paddingTop: "2.125rem", // 34px
};

const DECOR = (
  <>
    <PalmDecoration className={classes.palmTopLeft} />
    <PalmDecoration className={classes.palmBottomRight} />
  </>
);

// Small drag threshold (px) below which a pointerup is still treated as a plain click — lets
// mouse-drag scrolling coexist with the cards' own links instead of eating every click.
const DRAG_CLICK_THRESHOLD = 6;

// Real admin-managed deals (see HomePageLoader.js → charterApi.getCharterDeals) when there are
// any, otherwise the hardcoded placeholder list — same "don't leave the section empty before the
// backend has real data in every environment" fallback the other unified sections don't need
// (they already only ever use real API data).
export function CharterDealsCarousel({ data }) {
  const { getStringRes } = useLocaleContext();
  const navigate = useNavigate();
  const trackRef = useRef(null);
  const dragState = useRef(null);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [activeDot, setActiveDot] = useState(0);

  const text = (key) => getCharterText(getStringRes, key);

  const charterDeals = data?.length ? data : mockCharterDeals;
  const dotCount = Math.ceil(charterDeals.length / VISIBLE_COUNT);

  const updateScrollState = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    setCanScrollPrev(el.scrollLeft > 4);
    setCanScrollNext(el.scrollLeft < maxScroll - 4);
    if (dotCount > 1 && el.clientWidth > 0) {
      const page = Math.round(el.scrollLeft / el.clientWidth);
      setActiveDot(Math.min(dotCount - 1, Math.max(0, page)));
    }
  }, [dotCount]);

  useEffect(() => {
    updateScrollState();
    window.addEventListener("resize", updateScrollState);
    return () => window.removeEventListener("resize", updateScrollState);
  }, [updateScrollState]);

  // Arrows/dots move by one full "page" (the track's own visible width, i.e. ~4 cards) rather
  // than one card, so the side arrows and the pagination dots agree on what a "step" is.
  const scrollByPage = (direction) => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * el.clientWidth, behavior: "smooth" });
  };

  const scrollToDot = (index) => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollTo({ left: index * el.clientWidth, behavior: "smooth" });
  };

  // Mouse-drag-to-scroll — touch swipe and trackpad horizontal scroll already work natively via
  // the plain `overflow-x: auto` track, this just adds click-and-drag for a mouse.
  const onPointerDown = (e) => {
    if (e.pointerType === "touch") return; // native touch scrolling already handles this
    const el = trackRef.current;
    if (!el) return;
    dragState.current = {
      startX: e.clientX,
      startScrollLeft: el.scrollLeft,
      moved: 0,
      pointerId: e.pointerId,
      captured: false,
    };
  };

  const onPointerMove = (e) => {
    const el = trackRef.current;
    const drag = dragState.current;
    if (!el || !drag) return;
    const delta = e.clientX - drag.startX;
    drag.moved = Math.max(drag.moved, Math.abs(delta));
    // Only take pointer capture once an actual drag starts — capturing immediately on
    // pointerdown (as this used to) redirects the click event to this track div instead of
    // the card link underneath, silently breaking every card click with a mouse (desktop),
    // while touch is unaffected since it skips this handler entirely above.
    if (!drag.captured && drag.moved > DRAG_CLICK_THRESHOLD) {
      el.setPointerCapture(drag.pointerId);
      drag.captured = true;
    }
    el.scrollLeft = drag.startScrollLeft - delta;
  };

  const endDrag = () => {
    const el = trackRef.current;
    const drag = dragState.current;
    if (!el || !drag) return;
    if (drag.captured) el.releasePointerCapture(drag.pointerId);
    dragState.current = null;
  };

  // Suppresses the click-through to a card's link only when the pointerdown→up gesture actually
  // dragged past the threshold — a normal click still reaches the card link underneath.
  const onTrackClickCapture = (e) => {
    if (dragState.current?.moved > DRAG_CLICK_THRESHOLD) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  if (charterDeals.length === 0) return null;

  return (
    <ProductSection
      sectionStyle={SECTION_STYLE}
      sectionClassName={classes.section}
      decor={DECOR}
      icon={<VacationIcon size={72} />}
      title={text("charter.deals.title")}
      subtitle={text("charter.deals.description")}
      viewAllLabel={text("home.section.viewAll")}
      onViewAll={() => navigate("/charter-flights")}
      dotCount={dotCount}
      activeDot={activeDot}
      onDotClick={scrollToDot}>
      <div className={classes.carouselWrap}>
        {canScrollPrev && (
          <button
            type="button"
            aria-label="previous"
            className={classes.sideArrow}
            onClick={() => scrollByPage(-1)}>
            <IconChevronLeft size={20} stroke={1.75} />
          </button>
        )}
        <div
          className={classes.track}
          ref={trackRef}
          onScroll={updateScrollState}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onClickCapture={onTrackClickCapture}>
          {charterDeals.map((deal) => (
            <div key={deal.id} className={classes.cardWrap}>
              <CharterDealCard deal={deal} />
            </div>
          ))}
        </div>
        {canScrollNext && (
          <button
            type="button"
            aria-label="next"
            className={`${classes.sideArrow} ${classes.sideArrowNext}`}
            onClick={() => scrollByPage(1)}>
            <IconChevronRight size={20} stroke={1.75} />
          </button>
        )}
      </div>
    </ProductSection>
  );
}
